import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import {
  createVendorRequest,
  deleteVendorRequest,
  getVendorRequest,
  listVendorsRequest,
  updateVendorRequest,
} from "../../services/vendorsApi";
import Can from "../../components/auth/Can";
import TableRowActions from "../../components/common/TableRowActions";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";

function VendorsPage() {
  const { modulePermissions } = usePermissions();
  const perms = modulePermissions("vendor");
  const showActions = perms.canRead || perms.canUpdate || perms.canDelete;
  const tableColCount = showActions ? 4 : 3;

  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [deleteVendorId, setDeleteVendorId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const normalizeVendor = (vendor) => ({
    id: vendor?.id,
    name: vendor?.name || "",
    email: vendor?.email || "",
    phone: vendor?.phone || "",
    address: vendor?.address || "",
    created_at: (vendor?.created_at || "-").slice(0, 10),
    updated_at: (vendor?.updated_at || "-").slice(0, 10),
  });

  const extractVendors = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.vendors)) return payload.vendors;
    if (Array.isArray(payload?.data?.vendors)) return payload.data.vendors;
    return [];
  };

  const loadVendors = async () => {
    try {
      setIsLoading(true);
      const response = await listVendorsRequest({
        search: search.trim() || undefined,
        page,
        per_page: perPage,
      });
      const payload = response.data;
      const list = extractVendors(payload).map(normalizeVendor);
      setVendors(list);

      const fallbackTotal = list.length;
      const resolvedTotal =
        payload?.total ??
        payload?.meta?.total ??
        payload?.data?.total ??
        payload?.data?.meta?.total ??
        fallbackTotal;
      const resolvedLastPage =
        payload?.last_page ??
        payload?.meta?.last_page ??
        payload?.data?.last_page ??
        payload?.data?.meta?.last_page ??
        Math.max(1, Math.ceil(resolvedTotal / perPage));

      setTotalRecords(Number(resolvedTotal) || 0);
      setTotalPages(Number(resolvedLastPage) || 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load vendors.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, [page, search]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const openCreateModal = () => {
    setEditingVendor(null);
    setForm({ name: "", email: "", phone: "", address: "" });
    setIsModalOpen(true);
  };

  const openEditModal = async (vendor) => {
    setEditingVendor(vendor);
    setIsModalOpen(true);
    try {
      const response = await getVendorRequest(vendor.id);
      const payload = response.data?.data || response.data;
      const normalized = normalizeVendor(payload);
      setForm({
        name: normalized.name,
        email: normalized.email,
        phone: normalized.phone,
        address: normalized.address,
      });
    } catch {
      setForm({
        name: vendor.name,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
      });
    }
  };

  const openViewModal = async (vendor) => {
    try {
      const response = await getVendorRequest(vendor.id);
      const payload = response.data?.data || response.data;
      setViewingVendor(normalizeVendor(payload));
    } catch {
      setViewingVendor(vendor);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVendor(null);
    setForm({ name: "", email: "", phone: "", address: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
      };

      if (editingVendor?.id) {
        await updateVendorRequest(editingVendor.id, payload);
        toast.success("Vendor updated successfully.");
      } else {
        await createVendorRequest(payload);
        toast.success("Vendor created successfully.");
      }
      await loadVendors();
      closeModal();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingVendor ? "Failed to update vendor." : "Failed to create vendor."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteVendorId) return;
    try {
      await deleteVendorRequest(deleteVendorId);
      toast.success("Vendor deleted.");
      setDeleteVendorId(null);
      await loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete vendor.");
    }
  };

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Vendors</h1>
          <p className="ui-muted mt-2">
            Create, read, update, and delete vendors from one place.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <label className="input input-bordered relative flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border-base-300 bg-white pl-4 pr-3 sm:max-w-xl">
            <FiSearch className="h-4 w-4 shrink-0 text-base-content/45" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="grow bg-transparent outline-none placeholder:text-base-content/40 ui-nav-text"
              placeholder="Search vendors..."
              autoComplete="off"
            />
          </label>
          <Can permission={PERMISSIONS.VENDOR_CREATE}>
            <button type="button" className="ui-create-btn" onClick={openCreateModal}>
              <FiPlus className="ui-create-btn-icon" />
              Create vendor
            </button>
          </Can>
        </div>

        <div className="overflow-x-auto border-t border-base-200 px-4 sm:px-8">
          <table className="table table-zebra table-pin-rows table-md min-w-[920px] w-full">
            <thead className="border-b border-base-300 bg-base-200/70">
              <tr>
                <th scope="col" className="min-w-[12rem] ui-table-header">NAME</th>
                <th scope="col" className="min-w-[14rem] ui-table-header">EMAIL</th>
                <th scope="col" className="min-w-[10rem] ui-table-header">PHONE</th>
                {showActions ? (
                  <th scope="col" className="ui-table-header ui-table-actions-col">
                    ACTIONS
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {Array.from({ length: tableColCount }).map((__, innerIndex) => (
                        <td key={`cell-${innerIndex}`}>
                          <div className="skeleton my-2 h-4 w-full rounded-md" />
                        </td>
                      ))}
                    </tr>
                  ))
                : vendors.map((vendor) => (
                    <tr key={vendor.id}>
                      <td className="ui-nav-text font-semibold text-base-content">{vendor.name}</td>
                      <td className="ui-nav-text text-base-content/85">{vendor.email || "-"}</td>
                      <td className="ui-nav-text text-base-content/85">{vendor.phone || "-"}</td>
                      {showActions ? (
                        <td className="ui-table-actions-col">
                          <TableRowActions
                            readPermission={PERMISSIONS.VENDOR_READ}
                            updatePermission={PERMISSIONS.VENDOR_UPDATE}
                            deletePermission={PERMISSIONS.VENDOR_DELETE}
                            onView={() => openViewModal(vendor)}
                            onEdit={() => openEditModal(vendor)}
                            onDelete={() => setDeleteVendorId(vendor.id)}
                            viewLabel={`View ${vendor.name}`}
                            editLabel={`Edit ${vendor.name}`}
                            deleteLabel={`Delete ${vendor.name}`}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
              {!isLoading && vendors.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-14 align-middle">
                    <div className="flex w-full items-center justify-center">
                      <div className="alert inline-flex rounded-xl bg-base-200/70 px-6">
                        <span className="ui-nav-text text-center">No vendors found.</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-200 px-4 py-4 sm:px-8">
          <p className="ui-meta">
            Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm rounded-l-lg"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }).map((_, index) => {
              const current = index + 1;
              return (
                <button
                  key={current}
                  type="button"
                  className={`join-item btn btn-sm ${page === current ? "btn-active" : ""}`}
                  onClick={() => setPage(current)}
                >
                  {current}
                </button>
              );
            })}
            <button
              type="button"
              className="join-item btn btn-sm rounded-r-lg"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {isModalOpen ? (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-2xl rounded-2xl bg-white p-0">
            <div className="border-b border-base-300 px-6 py-5">
              <h3 className="ui-modal-title">
                {editingVendor ? "Update Vendor" : "Create Vendor"}
              </h3>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="form-control">
                <label className="label">
                  <span className="label-text ui-label-form">
                    Name <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="input input-bordered w-full rounded-xl"
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="form-control">
                  <label className="label"><span className="label-text ui-label-form">Email</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                    className="input input-bordered w-full rounded-xl"
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text ui-label-form">Phone</span></label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        phone: event.target.value.replace(/[^\d+]/g, ""),
                      }))
                    }
                    inputMode="numeric"
                    pattern="^\+?\d+$"
                    placeholder="e.g. 923001234567"
                    className="input input-bordered w-full rounded-xl"
                  />
                </div>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text ui-label-form">Address</span></label>
                <textarea
                  value={form.address}
                  onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))}
                  className="textarea textarea-bordered min-h-[110px] w-full rounded-xl"
                />
              </div>
              <div className="modal-action mt-0 border-t border-base-200 pt-4">
                <button type="button" className="btn btn-ghost rounded-xl" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary rounded-xl ${isSubmitting ? "btn-disabled" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? editingVendor
                      ? "Updating..."
                      : "Creating..."
                    : editingVendor
                      ? "Update Vendor"
                      : "Create Vendor"}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={closeModal}>close</button>
          </form>
        </dialog>
      ) : null}

      {viewingVendor ? (
        <dialog open className="modal modal-open">
          <div className="ui-detail-modal-box">
            <div className="ui-detail-modal-header">
              <h3 className="ui-modal-title">{viewingVendor.name}</h3>
              <p className="ui-muted mt-1">{viewingVendor.email || viewingVendor.phone || "Vendor profile"}</p>
            </div>
            <div className="ui-detail-grid">
              <div className="ui-detail-card">
                <p className="ui-meta">ID</p>
                <p className="ui-detail-value-strong">
                  {viewingVendor.id}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Name</p>
                <p className="ui-detail-value-strong">
                  {viewingVendor.name}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Email</p>
                <p className="ui-detail-value">{viewingVendor.email || "-"}</p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Phone</p>
                <p className="ui-detail-value">{viewingVendor.phone || "-"}</p>
              </div>
              <div className="ui-detail-card sm:col-span-2">
                <p className="ui-meta">Address</p>
                <p className="ui-detail-value">{viewingVendor.address || "-"}</p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Created At</p>
                <p className="ui-detail-value">{viewingVendor.created_at}</p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Updated At</p>
                <p className="ui-detail-value">{viewingVendor.updated_at}</p>
              </div>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-primary rounded-xl px-6 text-primary-content"
                onClick={() => setViewingVendor(null)}
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setViewingVendor(null)}>close</button>
          </form>
        </dialog>
      ) : null}

      {deleteVendorId ? (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-md rounded-2xl bg-white p-0">
            <div className="border-b border-base-300 px-6 py-5">
              <h3 className="ui-modal-title">Delete Vendor</h3>
              <p className="ui-muted mt-2">Are you sure you want to delete this vendor?</p>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-ghost rounded-xl"
                onClick={() => setDeleteVendorId(null)}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-error rounded-xl" onClick={handleDelete}>
                Delete Vendor
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setDeleteVendorId(null)}>close</button>
          </form>
        </dialog>
      ) : null}
    </main>
  );
}

export default VendorsPage;
